import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  X, BookmarkPlus, Loader2, MousePointerClick,
  PoundSterling, Calendar, Receipt, Hash, Tag, Eye, FileText
} from "lucide-react";

type FieldKey = "amountLabel" | "dateLabel" | "vatLabel" | "invoiceLabel" | "keywords";

interface FieldConfig {
  key: FieldKey;
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  ringColor: string;
  placeholder: string;
}

const FIELDS: FieldConfig[] = [
  {
    key: "amountLabel",
    label: "Amount",
    icon: <PoundSterling className="w-3.5 h-3.5" />,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-300",
    ringColor: "ring-blue-400",
    placeholder: "Select the label near the total amount (e.g. 'Total Due')",
  },
  {
    key: "dateLabel",
    label: "Date",
    icon: <Calendar className="w-3.5 h-3.5" />,
    color: "text-emerald-700",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-300",
    ringColor: "ring-emerald-400",
    placeholder: "Select the label near the date (e.g. 'Invoice Date')",
  },
  {
    key: "vatLabel",
    label: "VAT",
    icon: <Receipt className="w-3.5 h-3.5" />,
    color: "text-purple-700",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-300",
    ringColor: "ring-purple-400",
    placeholder: "Select the label near the VAT amount (e.g. 'VAT')",
  },
  {
    key: "invoiceLabel",
    label: "Invoice #",
    icon: <Hash className="w-3.5 h-3.5" />,
    color: "text-amber-700",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-300",
    ringColor: "ring-amber-400",
    placeholder: "Select the label near the invoice number (e.g. 'Invoice No')",
  },
  {
    key: "keywords",
    label: "Keywords",
    icon: <Tag className="w-3.5 h-3.5" />,
    color: "text-rose-700",
    bgColor: "bg-rose-50",
    borderColor: "border-rose-300",
    ringColor: "ring-rose-400",
    placeholder: "Select unique text that identifies this supplier",
  },
];

const HIGHLIGHT_COLORS: Record<FieldKey, string> = {
  amountLabel: "bg-blue-200/60",
  dateLabel: "bg-emerald-200/60",
  vatLabel: "bg-purple-200/60",
  invoiceLabel: "bg-amber-200/60",
  keywords: "bg-rose-200/60",
};

interface TemplateTrainerProps {
  open: boolean;
  onClose: () => void;
  rawText: string;
  previewUrl: string;
  isImage: boolean;
  vendor: string;
  currency: string;
  receiptId: number;
  detectedLabels?: {
    amountLabel?: string;
    dateLabel?: string;
    vatLabel?: string;
    invoiceLabel?: string;
    identifierKeywords?: string[];
  };
}

export function TemplateTrainer({
  open,
  onClose,
  rawText,
  previewUrl,
  isImage,
  vendor,
  currency,
  receiptId,
  detectedLabels,
}: TemplateTrainerProps) {
  const { toast } = useToast();
  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
  const ocrRef = useRef<HTMLDivElement>(null);

  const [activeField, setActiveField] = useState<FieldKey | null>("amountLabel");
  const [fieldValues, setFieldValues] = useState<Record<FieldKey, string>>({
    amountLabel: "",
    dateLabel: "",
    vatLabel: "",
    invoiceLabel: "",
    keywords: "",
  });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (detectedLabels) {
      setFieldValues((prev) => ({
        ...prev,
        amountLabel: detectedLabels.amountLabel || prev.amountLabel,
        dateLabel: detectedLabels.dateLabel || prev.dateLabel,
        vatLabel: detectedLabels.vatLabel || prev.vatLabel,
        invoiceLabel: detectedLabels.invoiceLabel || prev.invoiceLabel,
        keywords: detectedLabels.identifierKeywords?.join("\n") || prev.keywords,
      }));
    }
  }, [detectedLabels]);

  const handleTextSelection = useCallback(() => {
    if (!activeField) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const selectedText = selection.toString().trim();
    if (!selectedText) return;

    if (ocrRef.current && !ocrRef.current.contains(selection.anchorNode)) return;

    setFieldValues((prev) => {
      if (activeField === "keywords") {
        const existing = prev.keywords.trim();
        const lines = existing ? existing.split("\n") : [];
        if (!lines.includes(selectedText)) {
          lines.push(selectedText);
        }
        return { ...prev, keywords: lines.join("\n") };
      }
      return { ...prev, [activeField]: selectedText };
    });

    const fieldConf = FIELDS.find((f) => f.key === activeField);
    toast({
      title: `${fieldConf?.label} label set`,
      description: `"${selectedText.length > 40 ? selectedText.substring(0, 40) + "…" : selectedText}"`,
    });

    const nextIndex = FIELDS.findIndex((f) => f.key === activeField);
    if (nextIndex < FIELDS.length - 1) {
      const nextEmpty = FIELDS.slice(nextIndex + 1).find((f) => !fieldValues[f.key]);
      if (nextEmpty) {
        setActiveField(nextEmpty.key);
      }
    }

    selection.removeAllRanges();
  }, [activeField, fieldValues, toast]);

  useEffect(() => {
    document.addEventListener("mouseup", handleTextSelection);
    return () => document.removeEventListener("mouseup", handleTextSelection);
  }, [handleTextSelection]);

  const handleSave = async () => {
    if (!vendor.trim()) {
      toast({ title: "Please set a vendor name first", variant: "destructive" });
      return;
    }
    if (!fieldValues.keywords.trim()) {
      toast({ title: "Please select at least one identifier keyword from the receipt", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/supplier-templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          supplierName: vendor.trim(),
          identifierKeywords: fieldValues.keywords.trim(),
          vendorName: vendor.trim(),
          defaultCurrency: currency || null,
          amountLabel: fieldValues.amountLabel.trim() || null,
          dateLabel: fieldValues.dateLabel.trim() || null,
          vatLabel: fieldValues.vatLabel.trim() || null,
          invoiceLabel: fieldValues.invoiceLabel.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to save template");
      }
      toast({ title: `Template saved for "${vendor.trim()}"` });
      onClose();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const renderHighlightedText = () => {
    const highlights: Array<{ text: string; field: FieldKey }> = [];
    for (const f of FIELDS) {
      const val = fieldValues[f.key];
      if (!val) continue;
      if (f.key === "keywords") {
        val.split("\n").filter(Boolean).forEach((kw) => {
          highlights.push({ text: kw, field: f.key });
        });
      } else {
        highlights.push({ text: val, field: f.key });
      }
    }

    if (highlights.length === 0) {
      return <span>{rawText}</span>;
    }

    const sortedHighlights = highlights
      .map((h) => {
        const idx = rawText.toLowerCase().indexOf(h.text.toLowerCase());
        return { ...h, index: idx };
      })
      .filter((h) => h.index >= 0)
      .sort((a, b) => a.index - b.index);

    if (sortedHighlights.length === 0) {
      return <span>{rawText}</span>;
    }

    const parts: React.ReactNode[] = [];
    let lastEnd = 0;

    for (const h of sortedHighlights) {
      if (h.index < lastEnd) continue;

      if (h.index > lastEnd) {
        parts.push(<span key={`t-${lastEnd}`}>{rawText.slice(lastEnd, h.index)}</span>);
      }

      const actualText = rawText.slice(h.index, h.index + h.text.length);
      parts.push(
        <mark
          key={`h-${h.index}`}
          className={`${HIGHLIGHT_COLORS[h.field]} rounded px-0.5 py-px`}
          title={FIELDS.find((f) => f.key === h.field)?.label}
        >
          {actualText}
        </mark>
      );
      lastEnd = h.index + h.text.length;
    }

    if (lastEnd < rawText.length) {
      parts.push(<span key={`t-${lastEnd}`}>{rawText.slice(lastEnd)}</span>);
    }

    return <>{parts}</>;
  };

  if (!open) return null;

  const activeConfig = FIELDS.find((f) => f.key === activeField);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl w-[95vw] h-[90vh] max-w-[1400px] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
          <div>
            <h2 className="text-lg font-display font-bold text-slate-900">Train Supplier Template</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Select a field below, then highlight the label text on the receipt to set it
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            {FIELDS.map((field) => {
              const isActive = activeField === field.key;
              const hasValue = !!fieldValues[field.key];
              return (
                <button
                  key={field.key}
                  onClick={() => setActiveField(isActive ? null : field.key)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                    border-2 transition-all
                    ${isActive
                      ? `${field.bgColor} ${field.color} ${field.borderColor} ring-2 ${field.ringColor} ring-offset-1`
                      : hasValue
                        ? `${field.bgColor} ${field.color} ${field.borderColor}`
                        : "bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300"
                    }
                  `}
                >
                  {field.icon}
                  {field.label}
                  {hasValue && <span className="ml-0.5">✓</span>}
                </button>
              );
            })}

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  showPreview
                    ? "bg-slate-200 text-slate-700 border-slate-300"
                    : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {showPreview ? <FileText className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showPreview ? "Show Text" : "Show Preview"}
              </button>
            </div>
          </div>

          {activeConfig && (
            <div className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg ${activeConfig.bgColor} ${activeConfig.color} text-xs`}>
              <MousePointerClick className="w-4 h-4 shrink-0" />
              <span>{activeConfig.placeholder}</span>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 flex">
          <div className="flex-1 min-w-0 flex flex-col border-r border-slate-200">
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50 shrink-0">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                {showPreview ? "File Preview" : "Receipt Text — highlight to select"}
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-auto bg-white">
              {showPreview ? (
                <div className="h-full bg-slate-100">
                  {isImage ? (
                    <img src={previewUrl} alt="Receipt" className="w-full h-full object-contain" />
                  ) : (
                    <iframe src={previewUrl} className="w-full h-full border-0" title="Receipt preview" />
                  )}
                </div>
              ) : (
                <div
                  ref={ocrRef}
                  className="p-6 text-sm leading-relaxed font-mono whitespace-pre-wrap text-slate-800 select-text cursor-text"
                  style={{ userSelect: "text" }}
                >
                  {renderHighlightedText()}
                </div>
              )}
            </div>
          </div>

          <div className="w-80 shrink-0 flex flex-col bg-slate-50 overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 shrink-0">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Captured Fields
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {FIELDS.map((field) => {
                const value = fieldValues[field.key];
                const isActive = activeField === field.key;
                return (
                  <div
                    key={field.key}
                    onClick={() => setActiveField(field.key)}
                    className={`rounded-lg border-2 p-3 cursor-pointer transition-all ${
                      isActive
                        ? `${field.borderColor} ${field.bgColor} shadow-sm`
                        : value
                          ? `border-slate-200 bg-white`
                          : "border-dashed border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className={`flex items-center gap-1.5 text-xs font-semibold ${value ? field.color : "text-slate-400"}`}>
                        {field.icon}
                        {field.label}
                      </div>
                      {value && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFieldValues((prev) => ({ ...prev, [field.key]: "" }));
                          }}
                          className="p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Clear"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {value ? (
                      <div className={`text-xs ${field.color} font-mono break-words whitespace-pre-wrap`}>
                        {field.key === "keywords"
                          ? value.split("\n").filter(Boolean).map((kw, i) => (
                              <div key={i} className="flex items-center gap-1 mb-0.5">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${field.bgColor} ${field.borderColor} border`} />
                                <span>{kw}</span>
                              </div>
                            ))
                          : `"${value}"`}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-400 italic">
                        {isActive ? "Highlight text on the left…" : "Click to activate, then select text"}
                      </p>
                    )}
                  </div>
                );
              })}

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-1.5">
                  <Tag className="w-3.5 h-3.5" />
                  Vendor
                </div>
                <p className="text-xs font-medium text-slate-800">{vendor || "—"}</p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-1.5">
                  <PoundSterling className="w-3.5 h-3.5" />
                  Currency
                </div>
                <p className="text-xs font-medium text-slate-800">{currency || "GBP"}</p>
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 bg-white shrink-0 space-y-2">
              <Button onClick={handleSave} disabled={saving} className="w-full gap-1.5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}
                {saving ? "Saving…" : "Save Template"}
              </Button>
              <Button variant="ghost" onClick={onClose} className="w-full text-slate-500">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
